/** biome-ignore-all lint/suspicious/noExplicitAny: amazon-cognito-identity-js does not export a usable CognitoUserSession type; the session bag is read through getters */
import {
  AuthenticationDetails,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number; // seconds
}
export interface SrpCallbacks {
  onNewPasswordRequired: (submit: (newPassword: string) => void) => void;
  onMfaRequired: (submit: (code: string) => void, type: 'SMS' | 'TOTP') => void;
}
export interface CognitoPoolConfig {
  userPoolId: string;
  clientId: string;
}

function toTokens(session: any): CognitoTokens {
  const access = session.getAccessToken();
  return {
    accessToken: access.getJwtToken(),
    idToken: session.getIdToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    expiresIn: Math.max(0, access.getExpiration() - Math.floor(Date.now() / 1000)),
  };
}

function makeUser(email: string, pool: CognitoPoolConfig): CognitoUser {
  return new CognitoUser({
    Username: email,
    Pool: new CognitoUserPool({ UserPoolId: pool.userPoolId, ClientId: pool.clientId }),
  });
}

export function signInWithPassword(
  email: string,
  password: string,
  pool: CognitoPoolConfig,
  cbs: SrpCallbacks
): Promise<CognitoTokens> {
  const user = makeUser(email, pool);
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(toTokens(session)),
      onFailure: (err) => reject(err),
      newPasswordRequired: () =>
        cbs.onNewPasswordRequired((newPassword) =>
          // Empty {} attributes assumes the pool requires no additional user attributes at
          // password-set time; if a pool ever requires resending attributes (e.g. name), this
          // challenge would need them passed here.
          user.completeNewPasswordChallenge(
            newPassword,
            {},
            {
              onSuccess: (s: any) => resolve(toTokens(s)),
              onFailure: reject,
            }
          )
        ),
      mfaRequired: () =>
        cbs.onMfaRequired(
          (code) =>
            user.sendMFACode(
              code,
              { onSuccess: (s: any) => resolve(toTokens(s)), onFailure: reject },
              'SMS_MFA'
            ),
          'SMS'
        ),
      totpRequired: () =>
        cbs.onMfaRequired(
          (code) =>
            user.sendMFACode(
              code,
              { onSuccess: (s: any) => resolve(toTokens(s)), onFailure: reject },
              'SOFTWARE_TOKEN_MFA'
            ),
          'TOTP'
        ),
    });
  });
}

/**
 * Changes a native user's password. Re-authenticates via SRP with the current password first
 * (rather than reusing the app's existing session tokens) so the call itself proves the caller
 * knows the current password — the same reasoning a re-auth prompt serves in any account
 * settings flow. The resulting throwaway session is only used to authorize `changePassword`;
 * the app's existing session is untouched and remains valid.
 */
export function changePassword(
  email: string,
  oldPassword: string,
  newPassword: string,
  pool: CognitoPoolConfig
): Promise<void> {
  const user = makeUser(email, pool);
  const details = new AuthenticationDetails({ Username: email, Password: oldPassword });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: () => {
        user.changePassword(oldPassword, newPassword, (err: unknown) => {
          if (err) {
            reject(err);
          } else {
            resolve();
          }
        });
      },
      onFailure: reject,
      // A temporary/expired current password can't authorize a change this way — the account
      // needs the normal sign-in challenge flow (which itself sets a permanent password).
      newPasswordRequired: () => reject(new Error('PasswordResetRequiredException')),
      mfaRequired: () => reject(new Error('MFA_REQUIRED_UNSUPPORTED')),
      totpRequired: () => reject(new Error('MFA_REQUIRED_UNSUPPORTED')),
    });
  });
}

/**
 * Starts the "forgot password" flow: Cognito emails/texts a confirmation code to the account on
 * file. No prior authentication needed — this is the recovery path for a user who can't sign in
 * at all. Resolves with the delivery destination (e.g. a masked email) so the UI can tell the
 * user where to look; never rejects for an unknown email (Cognito's own user-existence-error
 * protection returns a simulated delivery detail instead, so this can't be used to enumerate
 * accounts).
 */
export function forgotPassword(
  email: string,
  pool: CognitoPoolConfig
): Promise<{ destination?: string; deliveryMedium?: string }> {
  const user = makeUser(email, pool);
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: (data: any) =>
        resolve({
          destination: data?.CodeDeliveryDetails?.Destination,
          deliveryMedium: data?.CodeDeliveryDetails?.DeliveryMedium,
        }),
      onFailure: reject,
    });
  });
}

/** Completes the "forgot password" flow: submits the emailed/texted code plus a new password. */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string,
  pool: CognitoPoolConfig
): Promise<void> {
  const user = makeUser(email, pool);
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: reject,
    });
  });
}

export function refreshSession(
  refreshToken: string,
  pool: CognitoPoolConfig,
  username: string
): Promise<CognitoTokens> {
  const user = makeUser(username, pool);
  return new Promise((resolve, reject) =>
    user.refreshSession(
      new CognitoRefreshToken({ RefreshToken: refreshToken }),
      (err: unknown, session: any) => (err ? reject(err) : resolve(toTokens(session)))
    )
  );
}
